import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  MapPin,
  Layers,
  PackagePlus,
  Home,
  Building,
  Building2,
  Wine,
  Gift,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de cartons directement à domicile
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que charger des paquets de cartons volumineux dans
              votre véhicule personnel au milieu des rues résidentielles
              {" "}{city.aCity} est une contrainte logistique inutile,
              notre service de proximité s&apos;occupe de tout.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une dépose devant votre porte",
                body: "Nous acheminons l'intégralité de vos commandes à plat directement chez vous à Marly-le-Roi, ainsi que dans les communes limitrophes comme Louveciennes, Le Pecq, Mareil-Marly, L'Étang-la-Ville et Le Port-Marly.",
                Icon: MapPin,
              },
              {
                title: "La garantie de la ponctualité",
                body: "Nous fixons ensemble un créneau horaire précis et rigoureusement respecté pour vous éviter de bloquer votre journée à attendre le livreur.",
                Icon: Clock,
              },
              {
                title: "Un matériel livré au sec",
                body: "Toutes vos fournitures vous sont remises dans un état de propreté irréprochable, hermétiquement protégées face aux intempéries et prêtes à être montées immédiatement.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Kits prêts à l'emploi ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Kits prêts à l&apos;emploi
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos kits de déménagement « Prêts à l&apos;emploi » : la
            solution tout-en-un
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous éviter d&apos;oublier un accessoire indispensable
            au milieu de vos préparatifs, nous avons conçu des packs
            complets adaptés à la taille de votre logement.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {[
            {
              title: "Kit Studio / T1",
              body: "Spécialement calibré pour les petits volumes, il regroupe une sélection de cartons standards, de cartons pour livres lourds, un rouleau d'adhésif professionnel et du film à bulles pour vos objets fragiles.",
              Icon: Home,
            },
            {
              title: "Kit Appartement (T2 / T3)",
              body: "Le compromis idéal pour les jeunes couples, intégrant des cartons renforcés à double cannelure, des rouleaux de protection haute densité et un dévidoir pistolet pour assembler vos boîtes en un temps record.",
              Icon: Building,
            },
            {
              title: "Kit Maison Familiale (T4 et plus)",
              body: "Un volume généreux de fournitures comprenant de la cartonnerie technique, des cartons penderies verticaux pour vos vêtements sur cintres, et de larges volumes de calage pour vider toutes vos pièces.",
              Icon: Building2,
            },
            {
              title: "Kit Spécial Vaisselle",
              body: "Le pack indispensable pour la cuisine, composé de nos cartons dotés de croisillons rigides pour isoler et transporter vos verres et assiettes sans aucun risque de casse.",
              Icon: Wine,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6 flex flex-col"
            >
              <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="mt-3 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-2.5 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-marly-le-roi-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous nos kits
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-marly-le-roi-cta-shop"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Cartons à l&apos;unité
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Zoom Fragile ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Zoom fragile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Zoom Fragile : comment emballer vos objets sensibles et
            bibelots&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Les intérieurs {city.aCity} regorgent souvent de vaisselle
            de réception, de cadres photo et de collections précieuses.
            Voici la technique pour les sécuriser à 100 %.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* La technique de la momie */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Gift className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                La technique de la momie
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Pour vos bibelots, statues ou porcelaines, enveloppez
              chaque pièce individuellement dans notre film à bulles
              haute densité. Fixez le tout avec un morceau
              d&apos;adhésif. Ne laissez jamais deux objets fragiles
              en contact direct dans le même carton ; le frottement
              durant le transport peut provoquer des micro-rayures ou
              des fêlures.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Chaque pièce enveloppée seule
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Film à bulles haute densité
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Pas de contact entre fragiles
              </div>
            </div>
          </div>

          {/* Calage des angles */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Layers className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Le calage des angles et des vides
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Tapissez toujours le fond de votre carton renforcé avec
              une couche de papier kraft froissé ou de film à bulles
              pour créer un matelas amortisseur. Placez les objets les
              plus lourds au fond, puis comblez méthodiquement tous
              les espaces vides sur les côtés et sur le dessus. Si le
              contenu bouge ou fait du bruit lorsque vous soulevez le
              carton, ajoutez du calage. Le carton doit former un
              bloc compact et hermétique.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <PackagePlus className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Matelas amortisseur au fond
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Lourd en bas, léger au-dessus
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Aucun espace vide
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-marly-le-roi-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Voir film bulles &amp; kraft
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-marly-le-roi-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-marly-le-roi-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
